import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, 
  Clock, 
  Calendar, 
  FileText, 
  MoreVertical, 
  Info,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  File,
  User,
  Tag,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

const BitrixTimelineCard = ({ item, index, isLast }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Determinar tipo de item e ícone
  const getItemType = () => {
    if (item.TYPE_ID === 4 || item.TYPE_ID === '4') return 'comment'; // Comentário
    if (item.TYPE_ID === 1 || item.TYPE_ID === '1') return 'task'; // Tarefa
    if (item.TYPE_ID === 2 || item.TYPE_ID === '2') return 'call'; // Chamada
    if (item.TYPE_ID === 3 || item.TYPE_ID === '3') return 'email'; // Email
    return 'activity'; // Atividade genérica
  };

  const getIcon = () => {
    const type = getItemType();
    switch (type) {
      case 'comment':
        return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case 'task':
        return <Clock className="w-5 h-5 text-red-500" />;
      case 'call':
        return <Calendar className="w-5 h-5 text-green-500" />;
      case 'email':
        return <FileText className="w-5 h-5 text-purple-500" />;
      default:
        return <MessageCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeLabel = () => {
    const type = getItemType();
    switch (type) {
      case 'comment': return 'Comentário';
      case 'task': return 'Tarefa';
      case 'call': return 'Chamada';
      case 'email': return 'Email';
      default: return 'Atividade';
    }
  };

  // Formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return {
        date: `${day} de ${getMonthName(date.getMonth())} de ${year}`,
        time: `${hours}:${minutes}`,
        full: `${day}/${month}/${year}, ${hours}:${minutes}`
      };
    } catch (error) {
      return { date: '', time: '', full: dateString };
    }
  };

  const getMonthName = (month) => {
    const months = [
      'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
    ];
    return months[month];
  };

  const dateInfo = formatDate(item.CREATED || item.CREATED_TIME);

  // Limpar HTML e extrair texto útil
  const cleanHtml = (htmlString) => {
    if (!htmlString || typeof htmlString !== 'string') return '';
    
    let cleaned = htmlString;
    
    // Remover divs vazias e divs com IDs estranhos (main_mail_form_crm_...)
    cleaned = cleaned.replace(/<div[^>]*id="main_mail_form[^"]*"[^>]*>\s*<\/div>/gi, '');
    cleaned = cleaned.replace(/<div[^>]*id="main_mail_form[^"]*"[^>]*>/gi, '');
    
    // Remover divs vazias
    cleaned = cleaned.replace(/<div[^>]*>\s*<\/div>/gi, '');
    
    // Converter <br> e <br/> para quebras de linha
    cleaned = cleaned.replace(/<br\s*\/?>/gi, '\n');
    
    // Converter <p> para quebras de linha
    cleaned = cleaned.replace(/<\/p>/gi, '\n');
    cleaned = cleaned.replace(/<p[^>]*>/gi, '');
    
    // Converter listas
    cleaned = cleaned.replace(/<\/li>/gi, '\n');
    cleaned = cleaned.replace(/<li[^>]*>/gi, '• ');
    cleaned = cleaned.replace(/<\/ul>/gi, '\n');
    cleaned = cleaned.replace(/<ul[^>]*>/gi, '');
    cleaned = cleaned.replace(/<\/ol>/gi, '\n');
    cleaned = cleaned.replace(/<ol[^>]*>/gi, '');
    
    // Remover imagens mas manter alt text se existir
    cleaned = cleaned.replace(/<img[^>]*alt="([^"]*)"[^>]*>/gi, '[Imagem: $1]');
    cleaned = cleaned.replace(/<img[^>]*>/gi, '[Imagem]');
    
    // Remover links mas manter texto
    cleaned = cleaned.replace(/<a[^>]*href="([^"]*)"[^>]*>([^<]*)<\/a>/gi, '$2 ($1)');
    cleaned = cleaned.replace(/<a[^>]*>([^<]*)<\/a>/gi, '$1');
    
    // Remover outras tags HTML mas manter o conteúdo
    cleaned = cleaned.replace(/<[^>]+>/g, '');
    
    // Decodificar entidades HTML
    cleaned = cleaned.replace(/&nbsp;/g, ' ');
    cleaned = cleaned.replace(/&amp;/g, '&');
    cleaned = cleaned.replace(/&lt;/g, '<');
    cleaned = cleaned.replace(/&gt;/g, '>');
    cleaned = cleaned.replace(/&quot;/g, '"');
    cleaned = cleaned.replace(/&#39;/g, "'");
    cleaned = cleaned.replace(/&apos;/g, "'");
    
    // Limpar quebras de linha e espaços excessivos
    cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n');
    cleaned = cleaned.replace(/[ \t]+/g, ' ');
    cleaned = cleaned.replace(/\n[ \t]+/g, '\n');
    cleaned = cleaned.replace(/[ \t]+\n/g, '\n');
    
    // Remover linhas vazias no início e fim
    cleaned = cleaned.trim();
    
    // Remover "undefined" que pode aparecer
    cleaned = cleaned.replace(/\bundefined\b/gi, '').trim();
    
    return cleaned;
  };

  // Extrair texto do comentário/atividade
  const getText = () => {
    let text = '';
    
    if (item.TEXT) text = item.TEXT;
    else if (item.DESCRIPTION) text = item.DESCRIPTION;
    else if (item.SUBJECT) text = item.SUBJECT;
    else if (item.COMMENT) text = item.COMMENT;
    
    // Se o texto contém HTML, limpar
    if (text && typeof text === 'string' && (text.includes('<div') || text.includes('<br') || text.includes('<p'))) {
      text = cleanHtml(text);
    }
    
    // Remover "undefined" do texto
    if (text) {
      text = text.replace(/undefined/g, '').trim();
    }
    
    return text || 'Sem conteúdo';
  };

  const text = getText();

  // Obter campos UF_CRM apenas do próprio item (atividade/comentário)
  // Campos UF_CRM do deal são exibidos separadamente na página
  const getUfCrmFields = () => {
    const ufFields = {};
    
    // Apenas campos UF_CRM do próprio item (atividade/comentário)
    Object.keys(item).forEach(key => {
      if (key.startsWith('UF_CRM') || key.startsWith('UF_DEAL') || key.startsWith('UF_ACTIVITY')) {
        if (item[key] !== null && item[key] !== undefined && item[key] !== '') {
          ufFields[key] = item[key];
        }
      }
    });
    
    return ufFields;
  };

  // Obter todos os campos do item (exceto os já exibidos e UF_CRM)
  const getAdditionalFields = () => {
    const excluded = ['TEXT', 'DESCRIPTION', 'SUBJECT', 'COMMENT', 'CREATED', 'CREATED_TIME', 'AUTHOR_ID', 'AUTHOR_NAME', 'FILES', 'TYPE_ID', 'ID', 'OWNER_ID', 'BITRIX_INSTANCE'];
    const fields = {};
    
    Object.keys(item).forEach(key => {
      // Excluir campos UF_CRM (já exibidos separadamente) e campos excluídos
      if (!key.startsWith('UF_CRM') && !key.startsWith('UF_DEAL') && !key.startsWith('UF_ACTIVITY') &&
          !excluded.includes(key) && item[key] !== null && item[key] !== undefined && item[key] !== '') {
        fields[key] = item[key];
      }
    });
    
    return fields;
  };

  const ufCrmFields = getUfCrmFields();
  const additionalFields = getAdditionalFields();

  // Mapear nomes amigáveis para campos UF_CRM conhecidos
  const getUfCrmFieldLabel = (key) => {
    const labels = {
      'UF_CRM_1746406184': '📊 Resumo da Venda',
      'UF_CRM_1559168142': '📊 Resumo de Vendas (Completo)',
      'UF_CRM_1561394928': '🖥️ Campo PC',
      'UF_CRM_1752752091': '🏢 Franqueado',
      'UF_CRM_1730750253': '📄 Contrato possuí franqueado',
      'UF_CRM_DEAL_1721602576682': '🔄 Espelhamento com franqueado',
      'UF_CRM_1731325756': '📝 Franqueado (Texto)',
      'UF_CRM_1754058580': '🔗 Link Anexo Contrato',
      'UF_CRM_1744295310': 'Campo Personalizado 1',
      'UF_CRM_1745494235': 'Campo Personalizado 2',
      'UF_CRM_1746139160': 'Campo Personalizado 3',
      'UF_CRM_1746140099': 'Campo Personalizado 4',
      'UF_CRM_1746140177': 'Campo Personalizado 5',
      'UF_CRM_1746140439': 'Campo Personalizado 6',
      'UF_CRM_1746140456': 'Campo Personalizado 7',
      'UF_CRM_1746146377': 'Campo Personalizado 8',
      'UF_CRM_1746146397': 'Campo Personalizado 9',
      'UF_CRM_1746146438': 'Campo Personalizado 10'
    };
    return labels[key] || key;
  };

  // Verificar se tem resumo de vendas para destacar
  const hasResumoVendas = ufCrmFields['UF_CRM_1746406184'];

  // Formatar valor do campo
  const formatFieldValue = (value) => {
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }
    return String(value);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="relative flex items-start space-x-4 mb-4"
    >
      {/* Linha do tempo vertical */}
      {!isLast && (
        <div className="absolute left-6 top-12 w-0.5 h-full bg-blue-200" />
      )}
      
      {/* Círculo da timeline */}
      <div className="relative z-10 flex-shrink-0">
        <div className="w-12 h-12 rounded-full bg-white border-2 border-blue-500 flex items-center justify-center shadow-sm">
          {getIcon()}
        </div>
      </div>

      {/* Card principal */}
      <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
        {/* Cabeçalho do card */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm font-medium text-gray-700">
                  {getTypeLabel()}
                </span>
                {dateInfo.time && (
                  <span className="text-xs text-gray-500">
                    {dateInfo.time}
                  </span>
                )}
              </div>
              
              {dateInfo.date && dateInfo.date !== 'undefined' && (
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <Calendar className="w-3 h-3" />
                  <span>{dateInfo.date}</span>
                </div>
              )}

              {item.AUTHOR_NAME && (
                <div className="mt-1 text-xs text-gray-600">
                  {item.AUTHOR_NAME}
                </div>
              )}
            </div>

            {/* Ícones de ação */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="p-1 rounded hover:bg-gray-100 transition-colors"
                title="Ver todos os detalhes"
              >
                <Info className={`w-4 h-4 ${showDetails ? 'text-blue-500' : 'text-gray-400'}`} />
              </button>
              <button
                className="p-1 rounded hover:bg-gray-100 transition-colors"
                title="Mais opções"
              >
                <MoreVertical className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Conteúdo do card */}
        <div className="p-4">
          <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
            {isExpanded || text.length < 200 ? (
              <div className="prose prose-sm max-w-none">
                {text.split('\n').map((line, idx) => (
                  <p key={idx} className={line.trim() ? 'mb-2' : 'mb-1'}>
                    {line.trim() || '\u00A0'}
                  </p>
                ))}
              </div>
            ) : (
              <>
                <div className="prose prose-sm max-w-none">
                  {text.substring(0, 200).split('\n').map((line, idx) => (
                    <p key={idx} className={line.trim() ? 'mb-2' : 'mb-1'}>
                      {line.trim() || '\u00A0'}
                    </p>
                  ))}
                </div>
                <span className="text-gray-500">...</span>
              </>
            )}
          </div>

          {/* Arquivos anexados */}
          {item.FILES && Object.keys(item.FILES).length > 0 && (
            <div className="mt-3 space-y-2">
              {Object.entries(item.FILES).map(([fileId, file]) => (
                <div
                  key={fileId}
                  className="flex items-center space-x-2 p-2 bg-gray-50 rounded border border-gray-200"
                >
                  <File className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-gray-700 flex-1 truncate">
                    {file.name || `Arquivo ${fileId}`}
                  </span>
                  {file.size && (
                    <span className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Campos UF_CRM - Destaque especial */}
          {Object.keys(ufCrmFields).length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="mb-2">
                <h4 className="text-xs font-semibold text-gray-700 flex items-center space-x-1">
                  <Tag className="w-3 h-3 text-orange-500" />
                  <span>Campos Personalizados (UF_CRM)</span>
                </h4>
              </div>
              {Object.entries(ufCrmFields).map(([key, value]) => {
                const isResumoVendas = key === 'UF_CRM_1746406184';
                return (
                  <div 
                    key={key} 
                    className={`p-3 rounded border ${
                      isResumoVendas 
                        ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-300 shadow-sm' 
                        : 'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <Tag className={`w-3 h-3 ${isResumoVendas ? 'text-orange-700' : 'text-orange-600'}`} />
                      <span className={`text-xs font-semibold ${isResumoVendas ? 'text-orange-900' : 'text-orange-900'}`}>
                        {getUfCrmFieldLabel(key)}:
                      </span>
                    </div>
                    <div className={`text-xs break-words whitespace-pre-wrap ${
                      isResumoVendas ? 'text-orange-900 font-medium' : 'text-orange-800'
                    }`}>
                      {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                    </div>
                    <div className="mt-1 text-xs text-orange-600 font-mono">
                      {key}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Informações adicionais */}
          {(item.SUBJECT || item.RESULT || item.STATUS || item.DIRECTION) && (
            <div className="mt-3 space-y-2">
              {item.SUBJECT && item.SUBJECT !== text && (
                <div className="p-2 bg-blue-50 rounded border border-blue-200">
                  <div className="flex items-center space-x-2 mb-1">
                    <Tag className="w-3 h-3 text-blue-600" />
                    <span className="text-xs font-medium text-blue-900">Assunto:</span>
                  </div>
                  <p className="text-xs text-blue-800">{item.SUBJECT}</p>
                </div>
              )}
              
              {item.RESULT && (
                <div className="p-2 bg-green-50 rounded border border-green-200">
                  <div className="flex items-center space-x-2 mb-1">
                    <CheckCircle className="w-3 h-3 text-green-600" />
                    <span className="text-xs font-medium text-green-900">Resultado:</span>
                  </div>
                  <p className="text-xs text-green-800">{item.RESULT}</p>
                </div>
              )}
              
              {item.STATUS && (
                <div className="p-2 bg-yellow-50 rounded border border-yellow-200">
                  <div className="flex items-center space-x-2 mb-1">
                    <AlertCircle className="w-3 h-3 text-yellow-600" />
                    <span className="text-xs font-medium text-yellow-900">Status:</span>
                  </div>
                  <p className="text-xs text-yellow-800">{item.STATUS}</p>
                </div>
              )}
              
              {item.DIRECTION && (
                <div className="p-2 bg-purple-50 rounded border border-purple-200">
                  <div className="flex items-center space-x-2 mb-1">
                    <User className="w-3 h-3 text-purple-600" />
                    <span className="text-xs font-medium text-purple-900">Direção:</span>
                  </div>
                  <p className="text-xs text-purple-800">
                    {item.DIRECTION === '1' ? 'Entrada' : item.DIRECTION === '2' ? 'Saída' : item.DIRECTION}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Seção de detalhes completos (expandível) */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-3 overflow-hidden"
              >
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold text-gray-700">Detalhes Completos</h4>
                    <span className="text-xs text-gray-500">ID: {item.ID}</span>
                  </div>
                  
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {Object.entries(additionalFields).map(([key, value]) => (
                      <div key={key} className="text-xs">
                        <span className="font-medium text-gray-600">{key}:</span>{' '}
                        <span className="text-gray-800 break-words">
                          {formatFieldValue(value)}
                        </span>
                      </div>
                    ))}
                    
                    {Object.keys(additionalFields).length === 0 && (
                      <p className="text-xs text-gray-500 italic">Nenhum campo adicional disponível</p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Rodapé com botões */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {text.length > 200 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-xs text-gray-600 hover:text-gray-900 flex items-center space-x-1 transition-colors"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    <span>Recolher</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    <span>Expandir</span>
                  </>
                )}
              </button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {item.FILES && Object.keys(item.FILES).length > 0 && (
              <button className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors flex items-center space-x-1">
                <File className="w-3 h-3" />
                <span>DOCUMENTOS</span>
              </button>
            )}
            <button 
              onClick={() => {
                const bitrixUrl = item.BITRIX_INSTANCE === 'old' 
                  ? 'https://villela.bitrix24.com.br'
                  : 'https://grupovillela.bitrix24.com.br';
                window.open(`${bitrixUrl}/crm/deal/details/${item.OWNER_ID || ''}/`, '_blank');
              }}
              className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors flex items-center space-x-1"
            >
              <ExternalLink className="w-3 h-3" />
              <span>ABRIR</span>
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BitrixTimelineCard;
